<script setup>
import { Link } from '@inertiajs/vue3'
import { ref } from 'vue'
import Pagination from '@/Shared/Pagination.vue'
import Portrait from '@/Shared/Portrait.vue'
import SearchInput from '@/Shared/SearchInput.vue'
import Icon from '@/Shared/Icon.vue'
import { useDate } from '@/Composables/useDate'

const props = defineProps({
  players: {
    type: Object,
    default: () => {}
  },
  filters: {
    type: Object,
    default: () => ({
      search: ''
    })
  }
})

const search = ref(props.filters.search)

const breadcrumbs = [{ label: 'Jugadores' }]

// Función para generar colores aleatorios pero consistentes basados en el ID del jugador
const getPlayerColor = (id) => {
  const colors = [
    '#4F46E5', // Indigo
    '#0EA5E9', // Sky
    '#10B981', // Emerald
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Violet
    '#EC4899', // Pink
    '#14B8A6' // Teal
  ]

  // Usar el ID para seleccionar un color de forma determinista
  return colors[id % colors.length]
}

// Calcular iniciales para avatar
const getInitials = (firstName, lastName) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`
}

// Función para formatear el número de teléfono
function formatPhone(phone) {
  if (!phone) return ''

  // Eliminar caracteres no numéricos
  const cleaned = phone.replace(/\D/g, '')

  // Formatear según el patrón español: XXX XX XX XX
  if (cleaned.length === 9) {
    return `${cleaned.substring(0, 3)} ${cleaned.substring(3, 5)} ${cleaned.substring(
      5,
      7
    )} ${cleaned.substring(7, 9)}`
  }

  return phone
}
</script>

<template>
  <Portrait title="Jugadores" subtitle="Gestiona los jugadores del club" :breadcrumbs="breadcrumbs">
    <!-- Barra de herramientas superior -->
    <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <SearchInput
        v-model="search"
        class="w-full sm:w-64"
        placeholder="Buscar jugadores..."
        url="/players"
        :debounce-time="250"
        :only="['players']"
      />

      <div class="flex space-x-3 items-stretch">
        <Link class="btn-indigo flex items-center justify-center w-full" href="/players/create">
          <Icon name="Plus" class="h-5 w-5 mr-2" />
          <span>Nuevo jugador</span>
        </Link>
      </div>
    </div>

    <!-- Lista de jugadores con diseño de tarjetas -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="player in players.data"
        :key="player.id"
        class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md hover:border-primary-300 cursor-pointer"
        @click="$inertia.visit(`/players/${player.id}/edit`)"
      >
        <!-- Cabecera de la tarjeta -->
        <div class="p-5 border-b border-gray-100 flex justify-between items-center">
          <div class="flex items-center space-x-3">
            <div
              class="rounded-full h-12 w-12 flex items-center justify-center font-bold text-white"
              :style="`background-color: ${getPlayerColor(player.id)};`"
            >
              {{ getInitials(player.name, player.lastname) }}
            </div>
            <div>
              <h3 class="font-semibold text-secondary-900">
                {{ player.name }} {{ player.lastname }}
              </h3>
              <div class="text-xs text-secondary-500">ID: {{ player.id }}</div>
            </div>
          </div>
        </div>

        <!-- Contenido de la tarjeta -->
        <div class="p-5 space-y-3">
          <!-- Teléfono -->
          <div class="flex items-center space-x-3">
            <div class="bg-blue-100 text-blue-600 p-2 rounded-lg">
              <Icon name="Phone" class="h-4 w-4" />
            </div>
            <div class="text-sm text-secondary-700">
              {{ formatPhone(player.phone) || 'No disponible' }}
            </div>
          </div>

          <!-- Email -->
          <div class="flex items-center space-x-3">
            <div class="bg-green-100 text-green-600 p-2 rounded-lg">
              <Icon name="Mail" class="h-4 w-4" />
            </div>
            <div class="text-sm text-secondary-700 truncate max-w-[200px]">
              {{ player.email || 'No disponible' }}
            </div>
          </div>

          <!-- Fecha de nacimiento / Edad -->
          <div class="flex items-center space-x-3">
            <div class="bg-amber-100 text-amber-600 p-2 rounded-lg">
              <Icon name="Calendar" class="h-4 w-4" />
            </div>
            <div class="text-sm text-secondary-700">
              {{ player.birthdate ? useDate(player.birthdate).diffForHumans() : 'No disponible' }}
            </div>
          </div>
        </div>

        <!-- Pie de la tarjeta -->
        <div class="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
          <span class="text-primary-600 text-sm font-medium flex items-center">
            Editar
            <Icon name="Pencil" class="h-4 w-4 ml-1" />
          </span>
        </div>
      </div>

      <!-- Estado vacío -->
      <div
        v-if="players.data.length === 0"
        class="col-span-full bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center"
      >
        <div class="flex flex-col items-center justify-center max-w-md mx-auto">
          <div class="bg-primary-50 p-4 rounded-full mb-4">
            <Icon name="User" class="h-10 w-10 text-primary-400" />
          </div>
          <h3 class="text-xl font-semibold text-secondary-900 mb-2">No hay jugadores todavía</h3>
          <p class="text-secondary-500 mb-6">
            Crea un nuevo jugador para comenzar a gestionar tu club
          </p>
          <Link class="btn-indigo flex items-center justify-center" href="/players/create">
            <Icon name="Plus" class="h-5 w-5 mr-2" />
            <span>Nuevo jugador</span>
          </Link>
        </div>
      </div>
    </div>

    <Pagination class="mt-6" :links="players.links" :preserve-state="true" :only="['players']" />
  </Portrait>
</template>
